import React from "react";
import { useLocation } from "react-router-dom";

import { faEllipsisH } from "@performyard/fontawesome/pro-regular-svg-icons";
import {
	AvatarGroup,
	AvatarProfile,
	Chip,
	DropDownMenu,
	DropDownMenuOption,
	Icon,
	Link,
	Text,
	TextDate,
} from "@performyard/py-components";
import type { ExtendedWorkspace } from "@performyard/service-contracts";

import ScrollableTable from "../../../../components/TableLite/TableLiteScrollable";

import { toAvatarGroupEmployees } from "../../utils/participants";

import EmptySearchTableMessage from "../../../../pageTemplates/InfoPageTemplate/EmptySearchTableMessage";

interface WorkspacesTableProps {
	workspaces: ExtendedWorkspace[];
	isFetchingWorkspaces: boolean;
	activeSort: string;
	activeSortDirection: string;
	handleColumnHeaderClick: (column: { name: string; direction: "asc" | "desc" }) => void;
	userId: string;
	handleEditWorkspace: (workspace: ExtendedWorkspace) => void;
	handleWorkspaceStatusChange: (workspaceId: string, status: "active" | "archived") => void;
}

const WorkspacesTable = (props: WorkspacesTableProps) => {
	const {
		workspaces,
		isFetchingWorkspaces,
		activeSort,
		activeSortDirection,
		handleColumnHeaderClick,
		userId,
		handleEditWorkspace,
		handleWorkspaceStatusChange,
	} = props;

	const location = useLocation();
	const currentSearchParams = location.search;

	const columns = [
		{
			id: "name",
			label: "Meeting Workspace Name",
			isSortable: true,
			width: 380,
			getter: (workspace: ExtendedWorkspace) => {
				const href = currentSearchParams
					? `/meeting-workspaces/${workspace._id}?from=${encodeURIComponent(currentSearchParams)}`
					: `/meeting-workspaces/${workspace._id}`;
				return (
					<Link href={href}>
						<Text
							size="paragraph-3"
							weight="regular"
							dangerouslySetClassName="py-line-clamp-1 max-w-[350px]"
						>
							{workspace.name}
						</Text>
					</Link>
				);
			},
		},
		{
			id: "status",
			label: "Status",
			isSortable: true,
			width: 88,
			getter: (workspace: ExtendedWorkspace) => {
				if (workspace.status === "active") {
					return <Chip label="Active" color="primary" size={24} />;
				}

				if (workspace.status === "archived") {
					return <Chip label="Archived" color="neutral" size={24} />;
				}
			},
		},
		{
			id: "creator",
			label: "Creator",
			isSortable: true,
			width: 148,
			getter: (workspace: ExtendedWorkspace) => {
				if (workspace.created?.userId && workspace.created?.name) {
					return (
						<AvatarProfile employeeId={workspace.created.userId} employeeName={workspace.created.name} />
					);
				}
			},
		},
		{
			id: "participants",
			label: "Participants",
			isSortable: false,
			width: 94,
			getter: (workspace: ExtendedWorkspace) => {
				const employees = toAvatarGroupEmployees(workspace.participants, { currentUserId: userId });
				return (
					<AvatarGroup
						employees={employees}
						hasLinks={true}
						maxLength={employees.length <= 5 ? 5 : 4}
						employeeNameTooltip={true}
						modalTitle="Workspace Participants"
						modalSubtitle={workspace.name}
					/>
				);
			},
		},
		{
			id: "previousMeeting",
			label: "Previous Meeting",
			isSortable: true,
			width: 128,
			getter: (workspace: ExtendedWorkspace) => {
				if (workspace.previousMeetingStartDateTime) {
					return <TextDate date={workspace.previousMeetingStartDateTime} hasTime={false} dateSize="medium" />;
				}
			},
		},
		{
			id: "upcomingMeeting",
			label: "Upcoming Meeting",
			isSortable: true,
			width: 140,
			getter: (workspace: ExtendedWorkspace) => {
				if (workspace.upcomingMeetingStartDateTime) {
					return <TextDate date={workspace.upcomingMeetingStartDateTime} hasTime={false} dateSize="medium" />;
				}
			},
		},
		{
			id: "control",
			label: "",
			isSortable: false,
			width: 36,
			getter: (workspace: ExtendedWorkspace) => {
				if (workspace.created.userId === userId) {
					const options: DropDownMenuOption[] = [
						{ key: "edit-key", label: "Edit", onClick: () => handleEditWorkspace(workspace) },
					];
					if (workspace.status === "active")
						options.push({
							key: "archive-key",
							label: "Archive",
							onClick: () => handleWorkspaceStatusChange(workspace._id, "archived"),
						});
					if (workspace.status === "archived")
						options.push({
							key: "activate-key",
							label: "Activate",
							onClick: () => handleWorkspaceStatusChange(workspace._id, "active"),
						});
					return (
						<div className="flex items-center justify-end">
							<DropDownMenu options={options} placement="bottom-end" className="group flex items-center">
								<Icon
									icon={faEllipsisH}
									size={24}
									color="neutral-7"
									dangerouslySetClassName="transition-colors group-hover:text-neutral-9"
								/>
							</DropDownMenu>
						</div>
					);
				}
				return <div className="h-8" />;
			},
		},
	];

	return (
		<div data-testid="workspacesContainer">
			<ScrollableTable
				rowKey="_id"
				columns={columns}
				isLoading={isFetchingWorkspaces}
				rows={workspaces}
				activeSort={activeSort}
				activeSortDirection={activeSortDirection}
				onColumnClick={(columnID, nextDirection) =>
					handleColumnHeaderClick({ name: columnID, direction: nextDirection as "asc" | "desc" })
				}
				emptyChildren={<EmptySearchTableMessage mainText="No Meeting Workspaces Found" />}
			/>
		</div>
	);
};

export default WorkspacesTable;
